import { useState, useEffect, useCallback, useMemo } from 'react';
import Modal from '../components/Modal';
import api from '../services/api';
import { formatToDisplayDate, parseDisplayDate } from '../services/dateFormatter';
import DateInput from '../components/DateInput';

export default function InvoiceForm({ initial, contracts, invoices, computeAllPeriods, role, onSubmit, onClose }) {
  const [form, setForm] = useState(initial || {});
  const [periodOptions, setPeriodOptions] = useState([]);
  const [customerQuery, setCustomerQuery] = useState('');
  const toDisplayDate = value => formatToDisplayDate(value);
  const toISODate = value => parseDisplayDate(value);
  const [similarInvoices, setSimilarInvoices] = useState([]);
  const [showSimilarWarning, setShowSimilarWarning] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (!initial) {
      setForm({});
      return;
    }
    // For new invoices with a pre-selected AMC, auto-fill description from the contract
    const isNew = !initial.id;
    let descriptionPrefill = initial.description || '';
    if (isNew && !descriptionPrefill && initial.amc && contracts && contracts.length > 0) {
      const amc = contracts.find(c => String(c.id) === String(initial.amc));
      if (amc) descriptionPrefill = amc.description || '';
    }
    setForm({
      ...initial,
      description: descriptionPrefill,
      invoice_date: formatToDisplayDate(initial.invoice_date),
      period_start: formatToDisplayDate(initial.period_start),
      period_end: formatToDisplayDate(initial.period_end),
    });
  }, [initial, contracts]);

  const recalc = useCallback((amcId, split, currentForm = form) => {
    const amc = contracts.find(c => String(c.id) === String(amcId));
    if (!amc) return { ...currentForm, amc: amcId, billing_split: split };
    const periods = computeAllPeriods(amc, split);
    const existingStarts = invoices.filter(i => String(i.amc) === String(amcId)).map(i => i.period_start);
    const pendingPeriods = periods.filter(period => !existingStarts.includes(period.period_start));
    setPeriodOptions(pendingPeriods);

    let idx = currentForm.period_index || 0;
    if (idx >= pendingPeriods.length) idx = 0;
    const chosen = pendingPeriods[idx] || pendingPeriods[0] || { period_start: '', period_end: '', amount: '', label: '' };
    return {
      ...currentForm,
      amc: amcId,
      billing_split: split,
      period_start: formatToDisplayDate(chosen.period_start),
      period_end: formatToDisplayDate(chosen.period_end),
      actual_amount: chosen.amount,
      period_index: idx,
    };
  }, [contracts, invoices, form, computeAllPeriods]);

  const uniqueCustomers = useMemo(() => {
    const map = new Map();
    contracts.forEach((contract) => {
      const customerId = contract.customer ?? contract.customer_id ?? contract.customer_name;
      const customerName = contract.customer_name || String(customerId || '');
      const key = String(customerId ?? customerName);
      if (!map.has(key)) {
        map.set(key, { id: key, name: customerName });
      }
    });
    return Array.from(map.values());
  }, [contracts]);

  const filteredCustomers = useMemo(() => {
    const query = customerQuery.trim().toLowerCase();
    return uniqueCustomers.filter((customer) => customer.name.toLowerCase().includes(query));
  }, [customerQuery, uniqueCustomers]);

  const selectedCustomerContracts = useMemo(() => {
    if (!form.customer && !isEdit) return [];
    if (isEdit) return contracts;
    return contracts.filter((contract) => {
      const customerId = String(contract.customer ?? contract.customer_id ?? contract.customer_name ?? '');
      if (customerId !== String(form.customer)) return false;
      const split = contract.installment || form.billing_split || 'Full';
      const periods = computeAllPeriods(contract, split);
      const existingStarts = invoices.filter(i => String(i.amc) === String(contract.id)).map(i => i.period_start);
      const pending = periods.some(period => !existingStarts.includes(period.period_start));
      return pending;
    });
  }, [contracts, form.customer, form.billing_split, invoices, isEdit, computeAllPeriods]);

  const handleCustomerChange = e => {
    const customerId = e.target.value;
    setCustomerQuery('');
    setForm(f => ({ ...f, customer: customerId, amc: '', period_index: 0, period_start: '', period_end: '', actual_amount: '' }));
    setPeriodOptions([]);
  };

  const handleCustomerSearchChange = e => {
    const query = e.target.value;
    setCustomerQuery(query);
    const selected = uniqueCustomers.find(c => c.name === query);
    if (selected && form.customer !== String(selected.id)) {
      handleCustomerChange({ target: { value: selected.id } });
    }
  };

  const handleAmcChange = e => setForm(f => {
    const amc = contracts.find(c => String(c.id) === String(e.target.value));
    const split = amc ? amc.installment || f.billing_split : f.billing_split;
    const nextForm = recalc(e.target.value, split, f);
    if (!nextForm.customer && amc) {
      nextForm.customer = String(amc.customer ?? amc.customer_id ?? amc.customer_name ?? '');
    }
    // Auto-fill description from AMC contract if not already set
    if (amc && !f.id && !f.description) {
      nextForm.description = amc.description || '';
    }
    return nextForm;
  });

  const handleSplitChange = e => setForm(f => recalc(f.amc, e.target.value, f));
  const handlePeriodIndexChange = e => {
    const idx = parseInt(e.target.value, 10);
    const p = periodOptions[idx];
    if (!p) return;
    setForm(f => ({ ...f, period_index: idx, period_start: formatToDisplayDate(p.period_start), period_end: formatToDisplayDate(p.period_end), actual_amount: p.amount }));
  };

  const submit = e => {
    e.preventDefault();
    const payload = { ...form };
    delete payload.billing_split;
    delete payload.period_index;
    delete payload.customer;

    const invoiceDateIso = payload.invoice_date ? parseDisplayDate(payload.invoice_date) : '';
    const periodStartIso = parseDisplayDate(payload.period_start);
    const periodEndIso = parseDisplayDate(payload.period_end);

    if (payload.invoice_date && !invoiceDateIso) {
      alert('Please enter Invoice Date in DD/MM/YYYY format.');
      return;
    }
    if (!periodStartIso || !periodEndIso) {
      alert('Please enter valid Period From and Period To dates in DD/MM/YYYY format.');
      return;
    }

    payload.invoice_date = invoiceDateIso || null;
    payload.period_start = periodStartIso;
    payload.period_end = periodEndIso;

    // Client-side similarity check before submitting
    const doSubmit = async () => {
      try {
        const res = await api.post('invoices/check-similar/', {
          customer_id: payload.customer,
          amount: payload.actual_amount,
          period_start: payload.period_start,
        });
        const similar = res.data.similar_invoices || [];
        if (similar.length > 0) {
          setSimilarInvoices(similar);
          setPendingPayload(payload);
          setShowSimilarWarning(true);
          // user must confirm in the modal to proceed
          return;
        }
      } catch (_) {
        // ignore API failure and allow submit
      }
      onSubmit(payload);
    };
    doSubmit();
  };

  const confirmProceed = async () => {
    if (!pendingPayload) return;
    try { await onSubmit(pendingPayload); } catch (_) {}
    setShowSimilarWarning(false);
    setPendingPayload(null);
  };

  const fmt = n => "₹" + Math.round(parseFloat(n) || 0).toLocaleString("en-IN");
  const base = parseFloat(form.actual_amount) || 0;
  const pct = parseFloat(form.tax_percentage) || 0;
  const tax = base * (pct / 100);
  const grand = base + tax;

  return (
    <>
    <Modal title={form.id ? "EDIT LEDGER INVOICE" : "GENERATE LEDGER INVOICE"} titleColor="emerald" onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        {!isEdit && (
          <div className="field full-width">
            <label>Customer</label>
            <input
              type="text"
              list="customer-list"
              placeholder="Search or select customer..."
              value={customerQuery}
              onChange={handleCustomerSearchChange}
              style={{ width: '100%' }}
            />
            <datalist id="customer-list">
              {filteredCustomers.map(customer => (
                <option key={customer.id} value={customer.name} />
              ))}
            </datalist>
          </div>
        )}

        <div className="grid-2">
          <div className="field"><label>AMC Agreement</label>
            <select required value={form.amc || ''} onChange={handleAmcChange} disabled={!form.customer && !isEdit}>
              <option value="">— Select AMC —</option>
              {selectedCustomerContracts.map(c => (
                <option key={c.id} value={c.id}>{`AMC-${c.id} — ${c.customer_name||''}`}</option>
              ))}
            </select>
            {form.customer && selectedCustomerContracts.length === 0 && (
              <div className="field-note">No agreements found for this customer.</div>
            )}
          </div>
          <div className="field"><label>Billing Split</label>
            <select value={form.billing_split || 'Full'} onChange={handleSplitChange}>
              <option>Full</option><option>Half-Yearly</option><option>Quarterly</option><option>Monthly</option>
            </select>
          </div>
        </div>

        {periodOptions.length > 0 && (
          <div className="field">
            <label>Installment</label>
            <select value={form.period_index || 0} onChange={handlePeriodIndexChange}>
              {periodOptions.map((p,i) => (
                <option key={i} value={i}>
                  {`${i+1} — ${formatToDisplayDate(p.period_start)} → ${formatToDisplayDate(p.period_end)}`}
                  {p.label ? ` (${p.label})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid-2">
          <div className="field"><label>Invoice Number</label>
            <input placeholder="e.g. IHRD/PMD/2026/001" value={form.invoice_number || ''} onChange={e => setForm(f => ({...f, invoice_number: e.target.value}))} disabled={form.invoice_locked && role !== 'Admin'} />
          </div>
          <div className="field"><label>Invoice Date</label>
            <DateInput value={form.invoice_date || ''} onChange={v => setForm(f => ({...f, invoice_date: v}))} disabled={form.invoice_locked && role !== 'Admin'} />
          </div>
        </div>

        <div className="grid-2">
          <div className="field"><label>Period From</label>
            <DateInput value={form.period_start || ''} onChange={v => setForm(f => ({...f, period_start: v}))} />
          </div>
          <div className="field"><label>Period To</label>
            <DateInput value={form.period_end || ''} onChange={v => setForm(f => ({...f, period_end: v}))} />
          </div>
        </div>

        <div className="tax-panel">
          <div className="tax-panel-title">Financial Split-Up Matrix</div>
          <div className="tax-field-grid">
            <div className="tax-field"><label>Actual Amount (Base ₹)</label>
              <input type="number" required value={form.actual_amount || ''} onChange={e => setForm(f => ({...f, actual_amount: e.target.value}))} placeholder="Auto-calculated" />
            </div>
            <div className="tax-field"><label>Tax Percentage (%)</label>
              <input type="number" value={form.tax_percentage || 18} onChange={e => setForm(f => ({...f, tax_percentage: e.target.value}))} />
            </div>
          </div>
          <div className="tax-row" style={{ paddingTop: '.5rem', borderTop: '1px solid #0f172a' }}>
            <span className="lbl">Calculated Tax:</span>
            <span className="val amber">{fmt(tax)}</span>
          </div>
          <div className="tax-row total">
            <span style={{ color: '#e2e8f0', fontWeight: 500 }}>Grand Total:</span>
            <span className="val emerald">{fmt(grand)}</span>
          </div>
        </div>

        <div className="field full-width"><label>Description</label>
          <textarea value={form.description || ''} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Additional notes, terms, or details for this invoice" rows="3" style={{ resize: 'none', width: '100%' }}></textarea>
        </div>

        <div className="field"><label>Settlement Status</label>
          <select value={form.status || 'Unpaid'} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
            <option>Unpaid</option><option>Partial</option><option>Paid</option>
          </select>
        </div>

        <button type="submit" className="btn btn-emerald" style={{ width: '100%', justifyContent: 'center', padding: '.7rem' }}>Post Tax Invoice</button>
      </form>
    </Modal>

    {showSimilarWarning && (
      <Modal title="Similar Invoices Found" titleColor="amber" onClose={() => setShowSimilarWarning(false)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          <div style={{ color: '#94a3b8' }}>
            {isEdit ? 'Invoice will be modified. Review before proceeding.' : 'The system detected invoices with similar period or amount for this customer. Review before proceeding.'}
          </div>
          <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '.5rem', padding: '.5rem', background: 'var(--surface)' }}>
            {similarInvoices.map(s => (
              <div key={s.id} style={{ padding: '.5rem', borderBottom: '1px solid var(--border-soft)' }}>
                <div style={{ fontWeight: 700 }}>{s.invoice_number}</div>
                <div style={{ color: '#94a3b8' }}>{s.period_start} → {s.period_end} • {fmt(s.actual_amount)}</div>
                <div style={{ color: '#cbd5e1', fontSize: '.85rem' }}>Reasons: {s.reasons.join(', ')}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end' }}>
            <button className="btn btn-slate" onClick={() => { setShowSimilarWarning(false); setPendingPayload(null); }}>Cancel</button>
            <button className="btn btn-emerald" onClick={async () => { await confirmProceed(); onClose(); }}>Proceed Anyway</button>
          </div>
        </div>
      </Modal>
    )}
    </>
  );
}
